import { useState, useEffect, useCallback, useRef } from "react";
import * as Sentry from "@sentry/react";
import Auth from "../../../Auth";
import { ImportOutlineContainer, ModalInnerContainer } from "./styles";
import Modal from "react-modal";
import IconButton from "../../IconButton";
import InlineSVG from "react-inlinesvg";
import { FileImportButton } from "../../FileImportButton";
import classNames from "classnames";

import useKeyboardShortcuts from "../../../hooks/useKeyboardShortcuts";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import InlineWarning from "../../InlineWarning";
import TextButton from "../../TextButton";
import PrimaryButton from "../../PrimaryButton";
import SVGIcon from "../../SVGIcon";
import { themeVariables } from "../../../themeVariables";
import { useStructuredOutlineStore } from "../../StoryBible/StructuredOutline/StructuredOutlineStore";
import { iconDefinitions } from "../../../iconDefinitions";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../../AppEventPublisher";

const IMPORT_OUTLINE_TIMEOUT_MS = 600000;
const IMPORT_OUTLINE_TIMEOUT_MESSAGE = "Importing this outline is taking too long. Please try again with a smaller outline, or paste the outline text instead of uploading the file.";

const modalOuterStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    zIndex: "30000",
    width: "80vw",
    height: "80vh",
    maxWidth: "836px",
    maxHeight: "510px",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.10), 0px 0px 2px 0px rgba(0, 0, 0, 0.25)",
    padding: "18px",
    overflow: "hidden",
    backgroundColor: "var(--menu-color)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
};

export default function ImportOutline({ isOpen, setIsOpen, projectId /**/ }) {
  const modalRef = useRef();
  const importAbortControllerRef = useRef(null);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importSessionId, setImportSessionId] = useState(null);
  const [disableFileImport, setDisableFileImport] = useState(false);
  const [importError, setImportError] = useState("");
  const [importStatus, setImportStatus] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const setOutline = useStructuredOutlineStore((state) => state.setOutline);

  const handleChange = (e) => {
    e.preventDefault();
    const text = e.target.value;

    setImportText(text);
  };

  // resets stat to start over import process
  const reset = useCallback(() => {
    importAbortControllerRef.current?.abort();
    importAbortControllerRef.current = null;
    setImportText("");
    setImportSessionId(null);
    setImporting(false);
    setDisableFileImport(false);
    setUploadingFile(false);
    setUploadName("");
    setImportError("");
    setImportStatus("");
  }, [setImportText, setImportSessionId, setImporting, setDisableFileImport, setUploadingFile, setUploadName, setImportError, setImportStatus]);

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onClose = () => {
    reset();
    setIsOpen(false);
  };

  const handleImport = async (textOutline, csvData) => {
    if (importing) return;
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, IMPORT_OUTLINE_TIMEOUT_MS);

    try {
      importAbortControllerRef.current = controller;
      setImporting(true);
      setDisableFileImport(true);
      setImportError("");
      setImportStatus(csvData ? "Importing CSV outline..." : "Formatting outline...");
      const body = { outline: textOutline, csvData, projectId };
      const result = await Auth.fetchWithIdToken("/api/ai/storyEngine/outline/import-outline", body, controller.signal, {
        requestSource: "import_outline",
        suppressAbortSentry: true,
      });
      if (result.success) {
        setOutline(result.outline);
        setImportError("");
        setIsOpen(false);
        AppEventPublisher.storyBible.action(StoryBibleType.outline, StoryBibleAction.Import);
      } else {
        setImportError(result.error || "We were unable to import your outline. Please check your formatting and try again.");
      }
    } catch (err) {
      if (controller.signal.aborted && !timedOut) {
        return;
      }

      if (!timedOut) {
        Sentry.captureException(err, { tags: { projectId } });
      }
      setImportError(timedOut ? IMPORT_OUTLINE_TIMEOUT_MESSAGE : err?.message || "We were unable to import your outline. Please check your formatting and try again.");
    } finally {
      clearTimeout(timeoutId);
      if (importAbortControllerRef.current === controller) {
        importAbortControllerRef.current = null;
      }
      setImporting(false);
      setDisableFileImport(false);
      setImportStatus("");
    }
  };

  const disableButton = importing || uploadingFile || (!importText && !importSessionId);

  useEffect(() => {
    // disable file import if we have text in the textarea
    // otherwise the backend won't know which to use
    if (importText && !disableFileImport) {
      setDisableFileImport(true);
    }

    if (!importText && disableFileImport) {
      setDisableFileImport(false);
    }
  }, [importText, disableFileImport]);

  // keyboard shortcuts
  const actions = {
    ctrlEnter: () => {
      if (isOpen && !disableButton) {
        handleImport(importText);
      }
    },
  };

  useKeyboardShortcuts({ actions });

  useOnClickOutside(modalRef, () => {
    if (isOpen) {
      onClose();
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      style={modalOuterStyles}
    >
      <ModalInnerContainer ref={modalRef}>
        <div className="header">
          <div className="header__title">
            <div className="main-title">Import your Outline</div>
            <div className="sub-title">You can import any outline. To generate an outline from a manuscript, use "Import" on the home page.</div>
          </div>
          <div className="close">
            <IconButton
              src="/assets/close.svg"
              onClick={onClose}
            />
          </div>
        </div>
        <ImportOutlineContainer>
          {!importError && (uploadingFile || importing) && (
            <div className="status">
              {uploadingFile ? "Converting file..." : importStatus || "Importing outline..."}
            </div>
          )}
          {!importError && (
            <div className="inputs">
              <FileImportButton
                setImportSessionId={setImportSessionId}
                handleValidate={() => {}}
                disable={disableFileImport}
                reset={reset}
                setUploading={setUploadingFile}
                uploadName={uploadName}
                setUploadName={setUploadName}
                onImportSuccess={({ text, parsed }) => {
                  setUploadingFile(false);
                  handleImport(text, parsed);
                }}
                onImportError={(error) => {
                  setUploadingFile(false);
                  setImportError(error);
                }}
              />
              <textarea
                className={classNames("paste-text", { disabled: uploadingFile || importing })}
                disabled={importing || uploadingFile}
                value={importText}
                onChange={handleChange}
                placeholder="Paste here..."
              />
            </div>
          )}

          {importError && (
            <div className="error">
              <InlineWarning text={importError} />
              <TextButton onClick={reset}>Reset</TextButton>
            </div>
          )}
        </ImportOutlineContainer>
        <div className="footer">
          <a
            href="/assets/sudowrite-outline-template.csv"
            download
            aria-label="Download outline import template"
            className="text-button-link"
          >
            <InlineSVG src="/assets/spreadsheet.svg" />
            <p>Download Template</p>
          </a>

          <PrimaryButton
            onClick={() => {
              handleImport(importText);
            }}
            ariaLabel="Import Outline"
            className="import-button"
            isLoading={importing || uploadingFile}
            loadingDuration={IMPORT_OUTLINE_TIMEOUT_MS / 1000}
            disabled={disableButton}
          >
            <SVGIcon
              icon={iconDefinitions.import}
              height={25}
              width={25}
              fillColor={themeVariables.ctaTextColor}
            />
            {importing ? "Importing" : uploadingFile ? "Converting" : "Import"}
          </PrimaryButton>
        </div>
      </ModalInnerContainer>
    </Modal>
  );
}
